(() => {
  'use strict';

  class AcciProduct {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;
      this.colorOptionIndex = parseInt(el.dataset.colorOptionIndex, 10);
      this.sizeOptionIndex = parseInt(el.dataset.sizeOptionIndex, 10);
      this.isMediaTransitioning = false;
      this.isComingSoon = el.dataset.comingSoon === 'true';
      this.registerResetTimeout = null;

      const dataEl = document.getElementById(`acci-product-data-${this.sectionId}`);
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[AcciProduct] Failed to parse variant JSON:', e, dataEl.textContent);
        return;
      }

      this.variants = this.data.variants;
      this.mediaByVariant = this.data.mediaByVariant;

      this.currentVariantId = this.data.currentVariantId;
      this.currentVariant = this.variants.find((v) => v.id === this.currentVariantId);

      this.selectedOptions = this.currentVariant ? [...this.currentVariant.options] : [];

      // If the URL specifies a variant that differs from what Liquid rendered, use the URL one.
      // This ensures colour tiles, size buttons, and the ATC button are all correct on load.
      const urlVariantId = parseInt(new URLSearchParams(window.location.search).get('variant'), 10);
      const hasUrlVariant = Boolean(urlVariantId) && this.variants.some((v) => v.id === urlVariantId);
      if (hasUrlVariant && urlVariantId !== this.currentVariantId) {
        const urlVariant = this.variants.find((v) => v.id === urlVariantId);
        this.currentVariant = urlVariant;
        this.currentVariantId = urlVariant.id;
        this.selectedOptions = [...urlVariant.options];
      }

      // Coming soon pieces start with no size chosen — the shopper must pick one before
      // registering interest, so the size they want is captured with the signup. null is
      // a wildcard in findVariant(), so price/gallery still track the rendered variant.
      // An explicit ?variant= link keeps its size (matches preselect_size in the section).
      if (this.isComingSoon && this.sizeOptionIndex >= 0 && !hasUrlVariant) {
        this.selectedOptions[this.sizeOptionIndex] = null;
      }

      this.initSizePicker();
      this.initColorPicker();
      this.initImageZoom();
      this.initGalleryImages();
      this.initGalleryVideos();
      this.initMobileGalleryCarousel();
      this.initAccordion();
      this.initSizeChart();
      this.initAddToCart();
      this.initRegisterInterest();
      this.syncVariantUI();
      this.playActiveMedia();
    }

    // ── Initial UI Sync ──────────────────────────────────────────────

    syncVariantUI() {
      if (!this.currentVariant) return;

      this.syncSizeButtons();
      this.updateSizeAvailability();
      this.updateColorUI();

      this.updateVariantInput();
      this.updatePrice();
      this.updateATCButton();
      this.updateGalleryForVariant(this.currentVariant, true);
    }

    // Reflect the selected size on the size buttons (no-op when there are no sizes).
    syncSizeButtons() {
      const sizesContainer = this.el.querySelector('.acci-product__sizes');
      if (!sizesContainer || this.sizeOptionIndex < 0) return;

      const currentSize = this.selectedOptions[this.sizeOptionIndex];
      sizesContainer.querySelectorAll('.acci-product__size-btn').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.sizeValue === currentSize);
      });
    }

    // ── Variant Helpers ──────────────────────────────────────────────

    findVariant(options) {
      return this.variants.find((v) => {
        return options.every((val, i) => {
          if (val === null) return true;
          return v.options[i] === val;
        });
      });
    }

    findVariantBySize(sizeValue) {
      const opts = [...this.selectedOptions];
      if (this.sizeOptionIndex >= 0) opts[this.sizeOptionIndex] = sizeValue;
      return this.findVariant(opts) || null;
    }

    getSelectedSize() {
      if (!(this.sizeOptionIndex >= 0)) return '';

      const fromState = this.selectedOptions[this.sizeOptionIndex] || '';
      const activeBtn = this.el.querySelector('.acci-product__size-btn.is-active');
      const size = fromState || (activeBtn && activeBtn.dataset.sizeValue) || '';

      if (!size || size === 'Default Title') return '';
      return size;
    }

    findVariantByColor(colorValue) {
      if (this.colorOptionIndex < 0) return null;

      // Prefer keeping the current size in the newly chosen colour.
      const opts = [...this.selectedOptions];
      opts[this.colorOptionIndex] = colorValue;
      const exact = this.findVariant(opts);
      if (exact && exact.available) return exact;

      // Current size isn't available in this colour — jump to its first available size.
      const firstAvail = this.variants.find(
        (v) => v.options[this.colorOptionIndex] === colorValue && v.available
      );
      if (firstAvail) return firstAvail;

      // Colour is fully sold out — still reflect the selection in the UI.
      return exact || this.variants.find((v) => v.options[this.colorOptionIndex] === colorValue) || null;
    }

    selectVariant(variant) {
      if (!variant) return;
      this.currentVariant = variant;
      this.currentVariantId = variant.id;
      this.selectedOptions = [...variant.options];

      this.updateVariantInput();
      this.updatePrice();
      this.updateATCButton();
      this.updateURL();
    }

    updateVariantInput() {
      const input = this.el.querySelector(`#acci-variant-id-${this.sectionId}`);
      if (input) input.value = this.currentVariantId;
    }

    updatePrice() {
      const priceEl = this.el.querySelector(`#acci-price-${this.sectionId} .acci-product__price-value`);
      if (!priceEl || !this.currentVariant) return;

      const formatter = new Intl.NumberFormat(
        document.documentElement.lang || 'en',
        { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }
      );

      let html = '';
      if (this.currentVariant.compare_at_price > this.currentVariant.price) {
        html += `<s class="acci-product__price-compare">${this.formatMoney(this.currentVariant.compare_at_price)}</s>`;
      }
      html += this.formatMoney(this.currentVariant.price);
      priceEl.innerHTML = html;
    }

    formatMoney(cents) {
      const amount = cents / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(amount);
      } catch {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }

    updateATCButton() {
      if (this.isComingSoon) return;

      const btn = this.el.querySelector(`#acci-atc-btn-${this.sectionId}`);
      const text = btn && btn.querySelector('.acci-product__atc-text');

      if (!btn || !this.currentVariant) return;

      if (this.currentVariant.available) {
        btn.disabled = false;
        if (text) text.textContent = 'Add to Cart';
      } else {
        btn.disabled = true;
        if (text) text.textContent = 'Sold Out';
      }
    }

    updateURL() {
      if (!this.currentVariant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariantId);
      window.history.replaceState({}, '', url.toString());
    }

    // ── Size Picker ──────────────────────────────────────────────────

    initSizePicker() {
      const sizesContainer = this.el.querySelector('.acci-product__sizes');
      if (!sizesContainer) return;

      sizesContainer.addEventListener('click', (e) => {
        const btn = e.target.closest('.acci-product__size-btn');
        if (!btn || (btn.classList.contains('is-sold-out') && !this.isComingSoon)) return;

        const sizeValue = btn.dataset.sizeValue;
        const variant = this.findVariantBySize(sizeValue);
        if (!variant) return;

        sizesContainer.querySelectorAll('.acci-product__size-btn').forEach((b) => {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        this.hideSizePrompt();

        this.selectVariant(variant);
      });
    }

    // ── Size Prompt (coming soon size gate) ──────────────────────────
    // Only rendered for coming soon products, so these are no-ops elsewhere.

    showSizePrompt() {
      const promptEl = this.el.querySelector(`#acci-size-prompt-${this.sectionId}`);
      if (!promptEl) return;
      promptEl.textContent = promptEl.dataset.message || '';
    }

    hideSizePrompt() {
      const promptEl = this.el.querySelector(`#acci-size-prompt-${this.sectionId}`);
      if (promptEl) promptEl.textContent = '';
    }

    // True when this product has a size axis but none is chosen yet.
    needsSizeSelection() {
      if (!this.el.querySelector('.acci-product__sizes')) return false;
      return !this.getSelectedSize();
    }

    updateSizeAvailability() {
      const sizesContainer = this.el.querySelector('.acci-product__sizes');
      if (!sizesContainer || this.sizeOptionIndex < 0) return;

      // Coming soon pieces aren't for sale yet, so every size stays selectable
      // and the chosen one is sent with the Register Interest signup.
      if (this.isComingSoon) {
        sizesContainer.querySelectorAll('.acci-product__size-btn').forEach((btn) => {
          btn.classList.remove('is-sold-out');
          btn.removeAttribute('aria-disabled');
        });
        return;
      }

      const currentColor =
        this.colorOptionIndex >= 0 ? this.selectedOptions[this.colorOptionIndex] : null;

      sizesContainer.querySelectorAll('.acci-product__size-btn').forEach((btn) => {
        const sizeValue = btn.dataset.sizeValue;
        const isAvailable = this.variants.some((v) => {
          const colorMatch = currentColor === null || v.options[this.colorOptionIndex] === currentColor;
          const sizeMatch = v.options[this.sizeOptionIndex] === sizeValue;
          return colorMatch && sizeMatch && v.available;
        });

        btn.classList.toggle('is-sold-out', !isAvailable);
        btn.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
      });
    }

    // ── Color Picker (native "Color"/"Colour" variant option) ────────
    // Sibling/meta colour products render the tiles as links/spans with no
    // [data-option-index], so this init is a no-op for them.

    initColorPicker() {
      const colorsContainer = this.el.querySelector('.acci-product__color-tiles[data-option-index]');
      if (!colorsContainer || this.colorOptionIndex < 0) return;

      colorsContainer.addEventListener('click', (e) => {
        const tile = e.target.closest('.acci-product__color-tile');
        if (!tile || tile.classList.contains('is-sold-out')) return;

        const colorValue = tile.dataset.colorValue;
        if (colorValue == null) return;

        const variant = this.findVariantByColor(colorValue);
        if (!variant) return;

        this.selectVariant(variant);
        // Changing colour can shift which size is selected and which sizes are in stock.
        this.syncSizeButtons();
        this.updateSizeAvailability();
        this.updateColorUI();
        this.updateGalleryForVariant(variant);
      });
    }

    updateColorUI() {
      if (this.colorOptionIndex < 0) return;
      const currentColor = this.selectedOptions[this.colorOptionIndex];

      const colorsContainer = this.el.querySelector('.acci-product__color-tiles[data-option-index]');
      if (colorsContainer) {
        colorsContainer.querySelectorAll('.acci-product__color-tile').forEach((tile) => {
          const isActive = tile.dataset.colorValue === currentColor;
          tile.classList.toggle('is-active', isActive);
          if (isActive) {
            tile.setAttribute('aria-current', 'true');
          } else {
            tile.removeAttribute('aria-current');
          }
        });
      }

      const nameEl = this.el.querySelector(`#acci-color-name-${this.sectionId}`);
      if (nameEl) nameEl.textContent = currentColor;
    }

    updateGalleryForVariant(variant, instant = false) {
      let mediaId = variant && this.mediaByVariant[variant.id];
      if (!mediaId) return;

      const slide = this.el.querySelector(`.acci-product__main-media[data-media-id="${mediaId}"]`);
      if (slide?.dataset.mediaType === 'video' || slide?.dataset.mediaType === 'external_video') {
        const firstImage = this.el.querySelector('.acci-product__main-media:not([data-media-type])');
        mediaId = firstImage?.dataset.mediaId;
      }

      if (mediaId) this.setActiveMedia(mediaId, instant);
    }

    isMobile() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    setActiveMedia(mediaId, instant = false) {
      if (this.isMobile()) {
        this.el.querySelectorAll('.acci-product__main-media').forEach((el) => {
          const willBeActive = el.dataset.mediaId === String(mediaId);
          const wasActive = el.classList.contains('is-active');
          el.classList.toggle('is-active', willBeActive);
          if (willBeActive && !wasActive) this.playMainMedia(el);
          if (!willBeActive && wasActive) this.pauseMainMedia(el);
        });
        this.updateGalleryImageState(mediaId);
        this.scrollCarouselToMedia(mediaId, instant);
        return;
      }

      this.resetImageZoom();

      const next = this.el.querySelector(`.acci-product__main-media[data-media-id="${String(mediaId)}"]`);
      const current = this.el.querySelector('.acci-product__main-media.is-active');

      if (!next) return;
      this.prefetchZoom(next);
      if (current === next) return;
      if (this.isMediaTransitioning) return;

      this.updateGalleryImageState(mediaId);

      if (!current) {
        next.classList.add('is-active');
        this.playMainMedia(next);
        return;
      }

      this.pauseMainMedia(current);

      this.isMediaTransitioning = true;
      this.el.classList.add('is-media-transitioning');

      next.classList.add('is-active', 'is-fading-in');
      this.playMainMedia(next);

      const durationMs = this.getCssDurationMs('--product-media-fade-duration', 150);
      const timeoutMs = durationMs + 200;

      const finish = () => {
        this.el.querySelectorAll('.acci-product__main-media').forEach((el) => {
          el.classList.toggle('is-active', el === next);
          el.classList.remove('is-fading-in');
        });

        this.isMediaTransitioning = false;
        this.el.classList.remove('is-media-transitioning');
      };

      let didFinish = false;
      const onEnd = (e) => {
        if (didFinish) return;
        if (e.target !== next) return;
        if (e.propertyName !== 'opacity') return;
        didFinish = true;
        next.removeEventListener('transitionend', onEnd);
        clearTimeout(fallback);
        finish();
      };

      next.addEventListener('transitionend', onEnd);
      const fallback = setTimeout(() => {
        if (didFinish) return;
        didFinish = true;
        next.removeEventListener('transitionend', onEnd);
        finish();
      }, timeoutMs);
    }

    updateGalleryImageState(mediaId) {
      this.el.querySelectorAll('.acci-product__gallery-image').forEach((el) => {
        const isActive = el.dataset.mediaId === String(mediaId);
        el.classList.toggle('is-active', isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    // ── Gallery Video Playback ──────────────────────────────────────

    playActiveMedia() {
      const active = this.el.querySelector('.acci-product__main-media.is-active');
      if (active) this.playMainMedia(active);
    }

    playMainMedia(mediaEl) {
      if (!mediaEl) return;

      if (mediaEl.dataset.mediaType === 'video') {
        const video = mediaEl.querySelector('video');
        if (!video) return;
        video.muted = true;
        this.matchGalleryVideoTime(mediaEl.dataset.mediaId, video);
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
        return;
      }

      if (mediaEl.dataset.mediaType === 'external_video') {
        const iframe = mediaEl.querySelector('iframe');
        if (!iframe || !iframe.contentWindow) return;
        if (iframe.classList.contains('js-youtube')) {
          iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else if (iframe.classList.contains('js-vimeo')) {
          iframe.contentWindow.postMessage('{"method":"play"}', '*');
        }
      }
    }

    pauseMainMedia(mediaEl) {
      if (!mediaEl) return;

      if (mediaEl.dataset.mediaType === 'video') {
        const video = mediaEl.querySelector('video');
        if (video) video.pause();
        return;
      }

      if (mediaEl.dataset.mediaType === 'external_video') {
        const iframe = mediaEl.querySelector('iframe');
        if (!iframe || !iframe.contentWindow) return;
        if (iframe.classList.contains('js-youtube')) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (iframe.classList.contains('js-vimeo')) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }
    }

    // The thumbnail keeps looping on its own, so the hero starts from wherever
    // the thumbnail is rather than restarting the clip.
    matchGalleryVideoTime(mediaId, heroVideo) {
      if (!mediaId || !heroVideo) return;

      const galleryVideo = this.el.querySelector(
        `.acci-product__gallery-image[data-media-id="${String(mediaId)}"] video`
      );
      if (!galleryVideo || !Number.isFinite(galleryVideo.currentTime)) return;

      const seek = () => {
        try {
          heroVideo.currentTime = galleryVideo.currentTime;
        } catch {
          /* Seeking is rejected until the media is seekable */
        }
      };

      if (heroVideo.readyState >= 1) {
        seek();
      } else {
        heroVideo.addEventListener('loadedmetadata', seek, { once: true });
      }
    }

    initGalleryVideos() {
      const videos = Array.from(this.el.querySelectorAll('.acci-product__gallery-video'));
      if (!videos.length) return;

      const playVideo = (video) => {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      };

      if (!('IntersectionObserver' in window)) {
        videos.forEach(playVideo);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              playVideo(entry.target);
            } else {
              entry.target.pause();
            }
          });
        },
        { threshold: 0.2 }
      );

      videos.forEach((video) => observer.observe(video));
    }

    getCssDurationMs(varName, fallbackMs) {
      const raw = getComputedStyle(this.el).getPropertyValue(varName).trim();
      if (!raw) return fallbackMs;
      const s = raw.toLowerCase();
      if (s.endsWith('ms')) {
        const n = parseFloat(s.slice(0, -2));
        return Number.isFinite(n) ? n : fallbackMs;
      }
      if (s.endsWith('s')) {
        const n = parseFloat(s.slice(0, -1));
        return Number.isFinite(n) ? n * 1000 : fallbackMs;
      }
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : fallbackMs;
    }

    initGalleryImages() {
      const galleryImages = this.el.querySelector('.acci-product__gallery-images');
      if (!galleryImages) return;

      galleryImages.addEventListener('mouseover', (event) => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        if (this.isMediaTransitioning) return;

        const item = event.target.closest('.acci-product__gallery-image');
        if (!item || !galleryImages.contains(item)) return;

        this.setActiveMedia(item.dataset.mediaId);
      });

      galleryImages.addEventListener('click', (event) => {
        if (window.matchMedia('(hover: hover)').matches) return;
        if (this.isMediaTransitioning) return;

        const item = event.target.closest('.acci-product__gallery-image');
        if (!item) return;

        this.setActiveMedia(item.dataset.mediaId);
      });
    }

    initMobileGalleryCarousel() {
      const track = this.el.querySelector('.acci-product__gallery-sticky');
      if (!track) return;
      this.galleryTrack = track;

      let raf = null;
      track.addEventListener(
        'scroll',
        () => {
          if (!this.isMobile()) return;
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = null;
            const slides = track.querySelectorAll('.acci-product__main-media');
            if (!slides.length || !track.clientWidth) return;
            const index = Math.max(
              0,
              Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth))
            );
            const mediaId = slides[index].dataset.mediaId;
            if (!mediaId) return;
            slides.forEach((slide, i) => {
              const willBeActive = i === index;
              const wasActive = slide.classList.contains('is-active');
              slide.classList.toggle('is-active', willBeActive);
              if (willBeActive && !wasActive) this.playMainMedia(slide);
              if (!willBeActive && wasActive) this.pauseMainMedia(slide);
            });
            this.updateGalleryImageState(mediaId);
          });
        },
        { passive: true }
      );
    }

    scrollCarouselToMedia(mediaId, instant = false) {
      const track = this.galleryTrack;
      if (!track) return;
      const slide = track.querySelector(`.acci-product__main-media[data-media-id="${String(mediaId)}"]`);
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: instant ? 'instant' : 'smooth' });
    }

    initImageZoom() {
      const galleryCol = this.el.querySelector('.acci-product__gallery-col');
      const lens = this.el.querySelector('.acci-product__zoom-lens');
      const zoomPanel = this.el.querySelector('.acci-product__zoom-panel');
      const zoomImg = this.el.querySelector('.acci-product__zoom-img');

      if (!galleryCol || !lens || !zoomPanel || !zoomImg) return;

      const getActiveMedia = () => this.el.querySelector('.acci-product__main-media.is-active');

      // Show the already-decoded inline image zoomed immediately, then swap in the
      // hi-res source once it has loaded (usually already warm from prefetchZoom, so
      // the swap is instant — this just removes the blank/soft gap when it isn't).
      const applyZoomSource = (activeMedia, img) => {
        const mediaId = activeMedia.dataset.mediaId || '';
        // Already handled for this media (upgrading or done) — nothing to do.
        if (zoomImg.dataset.mediaId === mediaId && zoomImg.dataset.zoomState) return;

        const lowRes = img.currentSrc || img.src;
        const hiRes = activeMedia.dataset.zoomSrc || lowRes;

        zoomImg.src = lowRes;
        zoomImg.dataset.mediaId = mediaId;

        if (hiRes === lowRes) {
          zoomImg.dataset.zoomState = 'high';
          return;
        }

        zoomImg.dataset.zoomState = 'loading';
        const loader = new Image();
        loader.decoding = 'async';
        const settle = (state) => {
          // Only upgrade if this media is still the one on screen.
          if (zoomImg.dataset.mediaId !== mediaId) return;
          if (state === 'high') zoomImg.src = hiRes;
          zoomImg.dataset.zoomState = 'high';
        };
        loader.onload = () => settle('high');
        loader.onerror = () => settle('low'); // keep the low-res fallback showing
        loader.src = hiRes;
      };

      const getLensDimensions = () => {
        const lensW = window.innerWidth * 0.15;
        const rightPanelW = window.innerWidth * 0.5;
        const rightPanelH = window.innerHeight;
        const lensH = lensW * (rightPanelH / rightPanelW);
        return { lensW, lensH };
      };

      const onMouseEnter = (event) => {
        if (window.innerWidth <= 768) return;

        const activeMedia = getActiveMedia();
        const img = activeMedia?.querySelector('.acci-product__main-img');
        if (!img || !img.complete || !img.naturalWidth) return;

        applyZoomSource(activeMedia, img);
        lens.style.display = 'block';
        zoomPanel.style.display = 'block';
      };

      const onMouseMove = (e) => {
        if (window.innerWidth <= 768) return;

        const activeMedia = getActiveMedia();
        const img = activeMedia?.querySelector('.acci-product__main-img');
        if (!activeMedia || !img || !img.complete || !img.naturalWidth) return;

        if (
          lens.style.display !== 'block' ||
          zoomPanel.style.display !== 'block' ||
          zoomImg.dataset.mediaId !== (activeMedia.dataset.mediaId || '')
        ) {
          applyZoomSource(activeMedia, img);
          lens.style.display = 'block';
          zoomPanel.style.display = 'block';
        }

        const galleryRect = galleryCol.getBoundingClientRect();
        const mediaRect = activeMedia.getBoundingClientRect();
        const { lensW, lensH } = getLensDimensions();

        const mouseX = e.clientX - mediaRect.left;
        const mouseY = e.clientY - mediaRect.top;

        let lensX = mouseX - lensW / 2;
        let lensY = mouseY - lensH / 2;
        lensX = Math.max(0, Math.min(mediaRect.width - lensW, lensX));
        lensY = Math.max(0, Math.min(mediaRect.height - lensH, lensY));

        lens.style.left = mediaRect.left - galleryRect.left + lensX + 'px';
        lens.style.top = mediaRect.top - galleryRect.top + lensY + 'px';
        lens.style.width = lensW + 'px';
        lens.style.height = lensH + 'px';

        const natW = img.naturalWidth;
        const natH = img.naturalHeight;
        const containerW = mediaRect.width;
        const containerH = mediaRect.height;
        const scale = Math.max(containerW / natW, containerH / natH);
        const displayedW = natW * scale;
        const displayedH = natH * scale;
        const offsetX = (containerW - displayedW) / 2;
        const offsetY = (containerH - displayedH) / 2;

        const imgX = lensX - offsetX;
        const imgY = lensY - offsetY;
        const zoomFactor = zoomPanel.getBoundingClientRect().width / lensW;

        zoomImg.style.width = displayedW * zoomFactor + 'px';
        zoomImg.style.height = displayedH * zoomFactor + 'px';
        zoomImg.style.left = -(imgX * zoomFactor) + 'px';
        zoomImg.style.top = -(imgY * zoomFactor) + 'px';
      };

      galleryCol.addEventListener('mouseenter', onMouseEnter);
      galleryCol.addEventListener('mousemove', onMouseMove);
      galleryCol.addEventListener('mouseleave', () => {
        this.resetImageZoom();
      });

      // Warm the active media's hi-res zoom source during idle time so the first
      // hover shows a sharp image immediately instead of waiting on a fresh fetch.
      const initialActive = getActiveMedia();
      if (initialActive) this.prefetchZoom(initialActive);
    }

    prefetchZoom(mediaEl) {
      if (this.isMobile()) return;
      const src = mediaEl && mediaEl.dataset ? mediaEl.dataset.zoomSrc : null;
      if (!src) return;
      this._zoomPrefetched = this._zoomPrefetched || new Set();
      if (this._zoomPrefetched.has(src)) return;
      this._zoomPrefetched.add(src);
      const warm = () => {
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 1500 });
      } else {
        setTimeout(warm, 300);
      }
    }

    resetImageZoom() {
      const lens = this.el.querySelector('.acci-product__zoom-lens');
      const zoomPanel = this.el.querySelector('.acci-product__zoom-panel');

      if (lens) lens.style.display = 'none';
      if (zoomPanel) zoomPanel.style.display = 'none';
      // Note: the zoom image's src/mediaId/zoomState are intentionally kept so that
      // re-hovering the same image shows the already-loaded hi-res instantly. When the
      // active media changes, applyZoomSource keys off the new media id and refreshes.
    }

    // ── Accordion ────────────────────────────────────────────────────

    initAccordion() {
      this.el.querySelectorAll('.acci-product__accordion-trigger').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('.acci-product__accordion-item');
          const content = item.querySelector('.acci-product__accordion-content');

          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          // Close all other open accordions first
          this.el.querySelectorAll('.acci-product__accordion-trigger[aria-expanded="true"]').forEach((openTrigger) => {
            if (openTrigger === trigger) return;
            const openItem = openTrigger.closest('.acci-product__accordion-item');
            const openContent = openItem.querySelector('.acci-product__accordion-content');
            openTrigger.setAttribute('aria-expanded', 'false');
            openContent.hidden = true;
          });

          if (isOpen) {
            trigger.setAttribute('aria-expanded', 'false');
            content.hidden = true;
          } else {
            trigger.setAttribute('aria-expanded', 'true');
            content.hidden = false;
          }
        });
      });
    }

    // ── Size Chart CSV Parsing ───────────────────────────────────────

    initSizeChart() {
      this.el.querySelectorAll('.acci-product__size-chart').forEach((chartEl) => {
        const raw = chartEl.dataset.sizeChart;
        if (!raw) return;

        const isUrl =
          raw.startsWith('http') ||
          raw.startsWith('//') ||
          raw.startsWith('/cdn/') ||
          raw.startsWith('/files/');
        if (isUrl) {
          const normalizedUrl = raw.startsWith('//') ? `${window.location.protocol}${raw}` : raw;
          const fetchUrl = normalizedUrl + (normalizedUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
          fetch(fetchUrl, { cache: 'no-store' })
            .then((r) => {
              if (!r.ok) throw new Error(`Failed to fetch size chart CSV (${r.status})`);
              return r.text();
            })
            .then((csv) => this.renderSizeChartTable(chartEl, csv))
            .catch(() => {});
        } else {
          this.renderSizeChartTable(chartEl, raw);
        }
      });
    }

    renderSizeChartTable(el, csv) {
      const rows = csv
        .trim()
        .split('\n')
        .map((r) => r.split(',').map((cell) => cell.trim()));

      if (!rows.length) return;

      const table = document.createElement('table');
      const thead = document.createElement('thead');
      const tbody = document.createElement('tbody');

      rows.forEach((cells, rowIndex) => {
        const tr = document.createElement('tr');
        cells.forEach((cell) => {
          const tag = rowIndex === 0 ? 'th' : 'td';
          const cellEl = document.createElement(tag);
          cellEl.textContent = cell;
          tr.appendChild(cellEl);
        });
        if (rowIndex === 0) {
          thead.appendChild(tr);
        } else {
          tbody.appendChild(tr);
        }
      });

      table.appendChild(thead);
      table.appendChild(tbody);
      el.appendChild(table);
    }

    // ── Add to Cart ──────────────────────────────────────────────────

    initAddToCart() {
      if (this.isComingSoon) return;

      const form = this.el.querySelector('.acci-product__form');
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const btn = form.querySelector('.acci-product__atc-btn');
        const textEl = btn && btn.querySelector('.acci-product__atc-text');
        const variantId = form.querySelector(`#acci-variant-id-${this.sectionId}`)?.value;

        if (!variantId || btn?.disabled || btn?.classList.contains('is-adding')) return;

        const originalText = textEl?.textContent || 'Add to Cart';
        if (btn) btn.classList.add('is-adding');
        if (textEl) textEl.textContent = 'Adding...';

        const startTime = Date.now();

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
          });

          if (!res.ok) throw new Error('Add to cart failed');

          const item = await res.json();
          
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item } }));

          const elapsed = Date.now() - startTime;
          const delay = Math.max(0, 1000 - elapsed);

          setTimeout(() => {
            if (btn) btn.classList.remove('is-adding');
            if (textEl) textEl.textContent = originalText;
            document.dispatchEvent(new CustomEvent('cart:open'));
          }, delay);

        } catch {
          if (textEl) textEl.textContent = 'Error — try again';
          setTimeout(() => {
            if (btn) btn.classList.remove('is-adding');
            if (textEl) textEl.textContent = originalText;
          }, 2000);
        }
      });
    }

    // ── Register Interest (coming soon) ─────────────────────────────

    initRegisterInterest() {
      if (!this.isComingSoon) return;

      const inner = this.el.querySelector('.acci-product__details-inner');
      const openBtn = this.el.querySelector(`#acci-register-open-${this.sectionId}`);
      const closeBtn = this.el.querySelector(`#acci-register-close-${this.sectionId}`);
      const form = this.el.querySelector(`#acci-register-form-${this.sectionId}`);
      const emailInput = this.el.querySelector(`#acci-register-email-${this.sectionId}`);
      const defaultContent = this.el.querySelector(`#acci-register-default-${this.sectionId}`);
      const successContent = this.el.querySelector(`#acci-register-success-${this.sectionId}`);
      const errorEl = this.el.querySelector(`#acci-register-error-${this.sectionId}`);

      if (!inner || !openBtn || !form || !emailInput) return;

      const openRegister = () => {
        // A size has to be chosen first — the register panel replaces the details
        // content (including the size picker), so it can't be picked once open.
        if (this.needsSizeSelection()) {
          this.showSizePrompt();
          const sizesContainer = this.el.querySelector(`#acci-sizes-${this.sectionId}`);
          if (sizesContainer) {
            const firstSize = sizesContainer.querySelector('.acci-product__size-btn:not(.is-sold-out)');
            if (firstSize) firstSize.focus();
          }
          return;
        }

        this.hideSizePrompt();
        inner.classList.add('is-registering');
        openBtn.setAttribute('aria-expanded', 'true');
        emailInput.focus();
      };

      const closeRegister = () => {
        clearTimeout(this.registerResetTimeout);
        this.registerResetTimeout = null;
        inner.classList.remove('is-registering');
        openBtn.setAttribute('aria-expanded', 'false');
        form.reset();
        form.classList.remove('is-submitting');
        if (errorEl) {
          errorEl.style.display = 'none';
          errorEl.textContent = '';
        }
        if (defaultContent) defaultContent.style.display = '';
        if (successContent) successContent.style.display = 'none';
        openBtn.focus();
      };

      openBtn.addEventListener('click', openRegister);
      if (closeBtn) closeBtn.addEventListener('click', closeRegister);

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (form.classList.contains('is-submitting')) return;

        // Belt and braces: the open button already gates on size, so this only fires if the
        // panel was opened some other way. Drop back to the picker rather than registering
        // interest with no size attached.
        if (this.needsSizeSelection()) {
          closeRegister();
          this.showSizePrompt();
          return;
        }

        const email = emailInput.value.trim();
        if (!email || !emailInput.checkValidity()) {
          if (errorEl) {
            errorEl.textContent = 'Please enter a valid email address.';
            errorEl.style.display = 'block';
          }
          return;
        }

        const companyId = form.dataset.klaviyoKey;
        const listId = form.dataset.klaviyoList;
        if (!companyId || !listId) {
          if (errorEl) {
            errorEl.textContent = 'Sign-up is not configured. Please try again later.';
            errorEl.style.display = 'block';
          }
          return;
        }

        form.classList.add('is-submitting');
        if (errorEl) errorEl.style.display = 'none';

        const productTitle = form.dataset.productTitle || '';
        const size = this.getSelectedSize();
        const interestLabel = size ? `${productTitle} — ${size}` : productTitle;
        const profileProperties = { 'Registered Interest': interestLabel };
        const eventProperties = {
          'Product Title': productTitle,
          'Product URL': window.location.href,
        };
        if (size) {
          profileProperties.Size = size;
          eventProperties.Size = size;
        }

        try {
          const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
            method: 'POST',
            headers: {
              accept: 'application/json',
              revision: '2024-02-15',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: size
                    ? `${form.dataset.customSource || 'Register Interest'} — ${size}`
                    : (form.dataset.customSource || 'Register Interest'),
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        properties: profileProperties,
                      },
                    },
                  },
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: listId,
                    },
                  },
                },
              },
            }),
          });

          if (response.ok || response.status === 202) {
            // The subscriptions endpoint silently drops profile properties for
            // already-subscribed profiles, so record a "Registered Interest"
            // event too — events always land and also update the profile.
            fetch(`https://a.klaviyo.com/client/events/?company_id=${companyId}`, {
              method: 'POST',
              headers: {
                accept: 'application/json',
                revision: '2024-02-15',
                'content-type': 'application/json',
              },
              body: JSON.stringify({
                data: {
                  type: 'event',
                  attributes: {
                    properties: eventProperties,
                    metric: {
                      data: {
                        type: 'metric',
                        attributes: { name: 'Registered Interest' },
                      },
                    },
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: {
                          email: email,
                          properties: profileProperties,
                        },
                      },
                    },
                  },
                },
              }),
            }).catch(() => {});

            if (defaultContent) defaultContent.style.display = 'none';
            if (successContent) successContent.style.display = 'block';
            this.registerResetTimeout = setTimeout(closeRegister, 3000);
          } else {
            const errData = await response.json().catch(() => null);
            throw new Error(errData?.errors?.[0]?.detail || 'Something went wrong. Please try again.');
          }
        } catch (err) {
          if (errorEl) {
            errorEl.textContent = err.message;
            errorEl.style.display = 'block';
          }
        } finally {
          form.classList.remove('is-submitting');
        }
      });
    }
  }

  // ── Init ────────────────────────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('.acci-product').forEach((el) => {
      if (!el.dataset.acciInit) {
        el.dataset.acciInit = 'true';
        new AcciProduct(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor re-init
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.acci-product');
    if (section && !section.dataset.acciInit) {
      section.dataset.acciInit = 'true';
      new AcciProduct(section);
    }
  });
})();
